import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit"
import {
  type PublicMessage,
  type Snapshot,
  UI_SURFACES_KEY,
  type VisibleBySlot,
  selectVisible,
  sl2Catalog,
} from "appsShared/inAppMessages"
import type { RootState } from "../../app/store"
import type { Sl2MessageContext } from "./sl2MessageContext"

/**
 * In-app messages consumer state — spec `dev/projects/in-app-messages.md` §Flows › Consume.
 * The snapshot arrives from IoT (routed by topic in `iot/routeMrtMessage.ts`); the context is
 * filled by `useMessageContext()`; dismissed / seen are read from browser storage once per mount.
 */
export interface InAppMessagesState {
  snapshot?: Snapshot
  context: Partial<Sl2MessageContext>
  /** `{ [messageId]: dismissedAt }`, both storages merged, plus dismissals made in this tab. */
  dismissed: Record<string, string>
  /** `{ [messageId]: firstSeenAt }` as read at load. Seen marks written later apply from the next visit. */
  seen: Record<string, string>
  /** Messages closed in this page session without a stored dismissal (a `dismiss: none` modal). */
  closed: Record<string, true>
  /** Feed items marked read in this tab (LOCKER-485). Kept apart from `seen`, which also drives `oncePerBrowser`. */
  feedRead: Record<string, string>
}

/**
 * True when `next` is older than `held`. Two writes can publish out of order, and a snapshot
 * built at an older live-set `version` lacks a change the newer one carries (DEV-451); an equal
 * version is a re-send (resubscribe, republish) and is kept. Without a version on both sides
 * (a snapshot published before DEV-451) `publishedAt` decides, as it did before.
 */
export function isOlderSnapshot(next: Snapshot, held: Snapshot): boolean {
  if (next.version !== undefined && held.version !== undefined)
    return next.version < held.version
  return held.publishedAt > next.publishedAt
}

const initialState: InAppMessagesState = {
  context: {},
  dismissed: {},
  seen: {},
  closed: {},
  feedRead: {},
}

export const inAppMessagesSlice = createSlice({
  name: "inAppMessages",
  initialState,
  reducers: {
    /** Takes an already-validated snapshot; `routeInAppMessagesSnapshot` is the only caller. */
    snapshotReceived: (state, action: PayloadAction<Snapshot>) => {
      if (state.snapshot && isOlderSnapshot(action.payload, state.snapshot))
        return
      state.snapshot = action.payload
    },
    contextChanged: (
      state,
      action: PayloadAction<Partial<Sl2MessageContext>>,
    ) => {
      state.context = action.payload
    },
    localStateLoaded: (
      state,
      action: PayloadAction<{
        dismissed: Record<string, string>
        seen: Record<string, string>
      }>,
    ) => {
      state.dismissed = { ...action.payload.dismissed, ...state.dismissed }
      state.seen = action.payload.seen
    },
    messageDismissed: (
      state,
      action: PayloadAction<{ id: string; at: string }>,
    ) => {
      state.dismissed[action.payload.id] = action.payload.at
    },
    messageClosed: (state, action: PayloadAction<string>) => {
      state.closed[action.payload] = true
    },
    feedMarkedRead: (
      state,
      action: PayloadAction<{ ids: string[]; at: string }>,
    ) => {
      action.payload.ids.forEach((id) => {
        state.feedRead[id] ??= action.payload.at
      })
    },
  },
})

export const inAppMessagesActions = inAppMessagesSlice.actions
export default inAppMessagesSlice.reducer

const selectState = (state: RootState) => state.inAppMessages

export const selectInAppSnapshot = (state: RootState) =>
  state.inAppMessages.snapshot

/** True when any live message needs `page.uiSurfaces`: an inline placement or a condition on it. */
export function snapshotUsesUiSurfaces(snapshot?: Snapshot): boolean {
  return !!snapshot?.messages.some(
    (m: PublicMessage) =>
      [m.placement.desktop, m.placement.tablet, m.placement.phone].some(
        (p) => typeof p === "object" && p.type === "inline",
      ) ||
      (m.audience.mode === "rules" &&
        m.audience.groups.some((g) =>
          g.conditions.some((c) => c.attribute === UI_SURFACES_KEY),
        )),
  )
}

const EMPTY: VisibleBySlot = {
  top: [],
  "below-header": [],
  bottom: [],
  toast: [],
  modal: [],
  panel: [],
  inline: [],
  feed: [],
}

/**
 * The shared engine over the store, memoised (spec §Engine). The viewport width is the
 * context's `device.width`, so a resize re-evaluates the placement per device (R19).
 */
export const selectVisibleInAppMessages = createSelector(
  [selectState],
  ({ snapshot, context, dismissed, seen, closed }): VisibleBySlot => {
    const width = context["device.width"]
    if (!snapshot || typeof width !== "number") return EMPTY
    // A stored dismissal counts only while the message is still dismissable: a banner
    // escalated in place to an outage (`dismiss: none`) shows again. A page-session close
    // (`closed`) still hides it until reload.
    const undismissable = new Set(snapshot.messages.filter((m) => m.dismiss === "none").map((m) => m.id))
    const hidden: Record<string, string> = {}
    Object.entries(dismissed).forEach(([id, at]) => {
      if (!undismissable.has(id)) hidden[id] = at
    })
    Object.keys(closed).forEach((id) => (hidden[id] ??= "closed"))
    return selectVisible({
      snapshot,
      context,
      catalog: sl2Catalog,
      dismissed: hidden,
      seen,
      viewportWidth: width,
    })
  },
)

/** Feed-slot ids not in `iam:sl2:seen` (as read at load) and not marked read in this tab. */
export const selectFeedUnreadIds = createSelector(
  [selectVisibleInAppMessages, selectState],
  ({ feed }, { seen, feedRead }): string[] =>
    feed.filter((m) => !seen[m.id] && !feedRead[m.id]).map((m) => m.id),
)
